import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import NotificationBell from '../NotificationBell/NotificationBell';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import useAuthorAvatar from '../../utils/useAuthorAvatar';
import { useSettings } from '../../context/SettingsContext';
import './Header.css';

const AccountDropdownItem = ({ username, accountType, profileImage, onSwitch }) => {
  const { avatarSources, handleAvatarError } = useAuthorAvatar({ author: username, profileImage });
  return (
    <button className="account-dropdown-item" onClick={onSwitch}>
      <img
        src={avatarSources[0] || ''}
        alt={username}
        className="account-dropdown-avatar"
        data-avatar-fallback-index="0"
        onError={handleAvatarError}
      />
      <span>{accountType === 'keychain' ? '⚡' : '🔑'} @{username}</span>
    </button>
  );
};

const Header = () => {
  const { user, isAuthenticated, logout, getSavedAccounts, loginWithSavedAccount, loginWithPostingKey, checkPromptUpdateStoredKey } = useAuth();
  const { startFeedPath } = useSettings();
  const { openLoginModal } = useLoginModal();
  const [showFeedsDropdown, setShowFeedsDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [quickLoginPin, setQuickLoginPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingKey, setIsUpdatingKey] = useState(false);
  const [newPostingKeyInput, setNewPostingKeyInput] = useState('');
  const dropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFeedsDropdown(false);
      }
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useBodyScrollLock(showPinModal);

  const handleAccountSwitch = async (username, accountType) => {
    setShowAccountDropdown(false);
    
    // Handle Keychain accounts - login directly without PIN
    if (accountType === 'keychain') {
      setPinError('');
      setIsLoading(true);
      try {
        await loginWithSavedAccount(username);
      } catch (err) {
        setPinError(err.message || 'Keychain login failed');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Handle posting key accounts - show PIN modal
    setSelectedAccount(username);
    setShowPinModal(true);
    setQuickLoginPin('');
    setIsUpdatingKey(false);
    setNewPostingKeyInput('');
    setPinError('');
  };

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    setPinError('');

    if (quickLoginPin.length !== 6) {
      setPinError('PIN must be 6 digits');
      return;
    }

    setIsLoading(true);
    try {
      if (isUpdatingKey) {
        if (!newPostingKeyInput.trim()) {
          setPinError('Please enter your new posting key');
          setIsLoading(false);
          return;
        }
        await loginWithPostingKey(selectedAccount, newPostingKeyInput.trim(), quickLoginPin);
        setShowPinModal(false);
        if (checkPromptUpdateStoredKey) {
          checkPromptUpdateStoredKey(selectedAccount, newPostingKeyInput.trim());
        }
      } else {
        await loginWithSavedAccount(selectedAccount, quickLoginPin);
        setShowPinModal(false);
      }
    } catch (err) {
      if (err.code === 'KEY_OUTDATED') {
        setIsUpdatingKey(true);
        setPinError('Your stored posting key is no longer valid or changed on Hive. Please enter your new posting key below.');
      } else {
        setPinError(err.message || 'Invalid PIN');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const savedAccounts = getSavedAccounts();
  const otherAccounts = Object.keys(savedAccounts).filter(username => username !== user?.username);
  const { avatarSources: userAvatarSources, handleAvatarError: handleUserAvatarError } = useAuthorAvatar({
    author: user?.username,
    profileImage: user?.profile?.profileImage,
  });

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo">
          <h1>{process.env.REACT_APP_NAME || 'Flexus'}</h1>
        </Link>

        <nav className="nav">
          <Link to={startFeedPath || '/'} className="nav-link">Home</Link>
          <Link to="/explore" className="nav-link">Explore</Link>
          <Link to="/trending" className="nav-link">Trending</Link>
          <Link to="/communities" className="nav-link">Communities</Link>
          {isAuthenticated && (
            <Link to="/following" className="nav-link">Following</Link>
          )}
          <div className="dropdown" ref={dropdownRef}>
            <button 
              className="nav-link dropdown-toggle" 
              onClick={() => setShowFeedsDropdown(!showFeedsDropdown)}
            >
              Feeds {showFeedsDropdown ? '▲' : '▼'}
            </button>
            {showFeedsDropdown && (
              <div className="dropdown-menu">
                <Link to="/feed/flex" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Flex
                </Link>
                <Link to="/feed/threads" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Threads
                </Link>
                <Link to="/feed/snaps" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Snaps
                </Link>
                <Link to="/feed/waves" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Waves
                </Link>
                <Link to="/feed/hangs" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Hangs
                </Link>
                <Link to="/feed/moments" className="dropdown-item" onClick={() => setShowFeedsDropdown(false)}>
                  Moments
                </Link>
              </div>
            )}
          </div>

          {isAuthenticated && (
            <Link to="/create-article" className="nav-link">Write Article</Link>
          )}
        </nav>

        <div className="auth-section">
          {isAuthenticated ? (
            <div className="user-menu">
              <NotificationBell />
              <Link to="/notifications" className="nav-link">Notifications</Link>
              <div className="account-dropdown" ref={accountDropdownRef}>
                <button 
                  className="avatar-btn"
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                >
                  <img
                    src={userAvatarSources[0] || ''}
                    alt={user.username}
                    className="user-avatar"
                    data-avatar-fallback-index="0"
                    onError={handleUserAvatarError}
                  />
                </button>
                {showAccountDropdown && (
                  <div className="account-dropdown-menu">
                    {otherAccounts.length > 0 && (
                      <>
                        <div className="account-dropdown-header">
                          <span>Switch Account</span>
                        </div>
                        {otherAccounts.map(username => {
                          const account = savedAccounts[username];
                          return (
                            <AccountDropdownItem
                              key={username}
                              username={username}
                              accountType={account.type || 'postingkey'}
                              profileImage={account.profileImage}
                              onSwitch={() => handleAccountSwitch(username, account.type || 'postingkey')}
                            />
                          );
                        })}
                      </>
                    )}
                    <button
                      className="account-dropdown-item add-account-btn"
                      onClick={() => {
                        setShowAccountDropdown(false);
                        openLoginModal();
                      }}
                    >
                      <span>➕ Add New Account</span>
                    </button>
                    <div className="account-dropdown-divider"></div>
                    <Link
                      to="/settings"
                      className="account-dropdown-item"
                      onClick={() => setShowAccountDropdown(false)}
                    >
                      <span>⚙️ Settings</span>
                    </Link>
                  </div>
                )}
              </div>
              <Link to={`/user/${user.username}`} className="user-link">
                @{user.username}
              </Link>
              <button onClick={logout} className="btn btn-secondary">
                Logout
              </button>
            </div>
          ) : (
            <button 
              onClick={openLoginModal} 
              className="btn btn-primary"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {showPinModal && (
        <div className="modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter PIN for @{selectedAccount}</h3>
              <button className="close-btn" onClick={() => setShowPinModal(false)}>×</button>
            </div>
            <form onSubmit={handleQuickLogin}>
              {pinError && <div className="error-message">{pinError}</div>}
              {isUpdatingKey && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    New Hive Posting Key
                  </label>
                  <input
                    type="password"
                    className="pin-input"
                    value={newPostingKeyInput}
                    onChange={(e) => setNewPostingKeyInput(e.target.value)}
                    placeholder="5K... (New posting key)"
                    autoFocus
                    disabled={isLoading}
                    style={{ fontSize: '0.9rem', letterSpacing: 'normal' }}
                  />
                </div>
              )}
              <div className="form-group">
                {isUpdatingKey && (
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    Enter 6-digit PIN to secure key
                  </label>
                )}
                <input
                  type="password"
                  className="pin-input"
                  value={quickLoginPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setQuickLoginPin(value);
                  }}
                  placeholder="••••••"
                  maxLength={6}
                  autoFocus={!isUpdatingKey}
                  disabled={isLoading}
                />
              </div>
              <button 
                type="submit"
                className="login-btn"
                disabled={isLoading || quickLoginPin.length !== 6 || (isUpdatingKey && !newPostingKeyInput.trim())}
              >
                {isLoading ? (isUpdatingKey ? 'Updating & Logging in...' : 'Logging in...') : (isUpdatingKey ? 'Update Key & Login' : 'Login')}
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => {
                    setIsUpdatingKey(!isUpdatingKey);
                    setPinError('');
                  }}
                >
                  {isUpdatingKey ? 'Cancel key update' : 'Posting key changed? Update it here'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
